import type { AdaptiveLogger } from "@1js/office-online-jsapi-core";
import {
  LogCategory,
  TraceLevel,
  createNewGuid,
  getAbsoluteTimeSinceEpoch,
  trySetPerformanceMark,
} from "@1js/office-online-jsapi-core";
import type { GraphDriveItem } from "../types";
import { bootDiagnostics } from "../types";
import { addHours } from "../utility";
import type {
  HttpHeaders,
  HttpRequestOptions,
  VroomRequestCacheItem,
} from "./types";
import { HttpRequestMethod } from "./types";

let cachedResponses: Map<
  string,
  VroomRequestCacheItem<GraphDriveItem | undefined>
>;
export function getDriveItem(
  url: string,
  requestConfig: Partial<HttpRequestOptions>,
  forceFetch = false,
  logger?: AdaptiveLogger,
  isRetryForSiteMove?: boolean,
  isGraph = false
): Promise<GraphDriveItem | undefined> {
  bootDiagnostics.driveItemStartTime = getAbsoluteTimeSinceEpoch();
  trySetPerformanceMark("driveItemStartTime");

  if (!cachedResponses) {
    cachedResponses = new Map<string, VroomRequestCacheItem<GraphDriveItem>>();
  }

  const cacheItem = cachedResponses.get(url);
  if (
    !forceFetch &&
    cacheItem &&
    cacheItem.promise &&
    (!cacheItem.expiryTime || cacheItem.expiryTime > new Date())
  ) {
    bootDiagnostics.driveItemEndTime = getAbsoluteTimeSinceEpoch();
    trySetPerformanceMark("driveItemEndTime");

    // ASSERTTAG_IGNORE_START
    logger?.ULS.sendTraceTag(
      0x21013150 /* tag_7atfq */,
      LogCategory.msoulscat_Wac_UnifiedUiHost,
      TraceLevel.Info,
      `GetDriveItem: Returning drive item from the cache`
    );
    // ASSERTTAG_IGNORE_FINISH
    return cacheItem.promise;
  }
  // ASSERTTAG_IGNORE_START
  logger?.ULS.sendTraceTag(
    0x21013151 /* tag_7atfr */,
    LogCategory.msoulscat_Wac_UnifiedUiHost,
    TraceLevel.Info,
    `GetDriveItem: Invalidating the cache`
  );
  // ASSERTTAG_IGNORE_FINISH
  cachedResponses.delete(url);

  // ASSERTTAG_IGNORE_START
  logger?.ULS.sendTraceTag(
    0x208e139d /* tag_697o3 */,
    LogCategory.msoulscat_Wac_UnifiedUiHost,
    TraceLevel.Info,
    `GetDriveItem: About to make the fetch call to get drive item`
  );
  // ASSERTTAG_IGNORE_FINISH

  let requestURL = url;
  let request: {
    method: HttpRequestMethod | undefined;
    headers: HttpHeaders | undefined;
    body?: string;
  } = {
    method: HttpRequestMethod.Get,
    headers: requestConfig.headers,
  };

  // Vroom calls will be POST requests, Graph calls will be GET requests
  if (!isGraph) {
    if (!isRetryForSiteMove) {
      // if we are retrying based on a site move, we don't want to add the ump query param, it is already added
      requestURL = url.concat("&ump=1");
    }
    const { body, headers } = getPOSTRequestBodyAndHeaders(
      requestConfig.headers
    );
    const method = HttpRequestMethod.Post;
    request = { method, headers, body };
    // ASSERTTAG_IGNORE_START
    logger?.ULS.sendTraceTag(
      0x1e56235d /* tag_4v8n3 */,
      LogCategory.msoulscat_Wac_UnifiedUiHost,
      TraceLevel.Info,
      `GetDriveItem: POST request to get drive item: content-length : ${body.length}`
    );
    // ASSERTTAG_IGNORE_FINISH
  }

  const fetchPromise = fetch(requestURL, request)
    .catch((error) => {
      return Promise.reject({
        api: "getItem",
        failure: error,
        message: "Fetch call failed",
        retry: true,
      });
    })
    .then(async (res) => {
      if (res.status === 200) {
        const graphItem: Promise<GraphDriveItem> = res.json().then((val) =>
          !!val
            ? val
            : Promise.reject({
                api: "getItem",
                status: res.status,
                message: "Ok httpStatus, invalid response",
              })
        );

        const vroomResponseHeaders = new Map();
        const spRequestGuid = res?.headers?.get("SPRequestGuid");
        bootDiagnostics.spRequestGuid = spRequestGuid ?? undefined;
        vroomResponseHeaders.set("SPRequestGuid", spRequestGuid);

        // ASSERTTAG_IGNORE_START
        logger?.ULS.sendTraceTag(
          0x21141483 /* tag_7fbsd */,
          LogCategory.msoulscat_Wac_UnifiedUiHost,
          TraceLevel.Info,
          `GetDriveItem:  [VroomResponseHeaders: ${JSON.stringify(
            vroomResponseHeaders
          )}]`
        );
        // ASSERTTAG_IGNORE_FINISH

        cachedResponses.set(url, {
          promise: fetchPromise,
          expiryTime: addHours(new Date(), 24),
        });

        return graphItem;
      }

      try {
        const response = await res.json();
        const spRequestGuid = res?.headers?.get("SPRequestGuid");
        bootDiagnostics.spRequestGuid = spRequestGuid ?? undefined;
        return Promise.reject({
          api: "getItem",
          SPRequestGuid: spRequestGuid,
          status: res.status,
          errorCode: response?.error?.code,
          innerErrorCode: response?.error?.innerError?.code,
          message: response?.error?.message,
          redirectLocation: response?.error
            ? response?.error["@error.redirectLocation"]
            : undefined,
          ...getClaimsChallengeError(res),
          retry: res.status === 429 || res.status >= 500,
        });
      } catch (e) {
        return Promise.reject({
          api: "getItem",
          status: res.status,
          message: "request failed, could not retrieve error object.",
        });
      }
    })
    .finally(() => {
      bootDiagnostics.driveItemEndTime = getAbsoluteTimeSinceEpoch();
      trySetPerformanceMark("driveItemEndTime");
    });

  return fetchPromise;
}

export function getClaimsChallengeError(res: Response) {
  const authenticate = res?.headers?.get("www-authenticate");
  let error: string | undefined = undefined;
  let claims: string[] | undefined = undefined;

  if (authenticate) {
    const fields = authenticate.split(",");

    if (fields) {
      for (const field of fields) {
        const data = field && field.trim();
        if (data && data.startsWith("error=")) {
          const temp = data.split('"');
          error = temp[1];
        } else if (data && data.startsWith("claims=")) {
          const temp = data.split('"');
          claims = [temp[1]];
        }
      }
    }
  }
  return { claimsError: error, claims };
}

function getPOSTRequestBodyAndHeaders(headers?: { [index: string]: string }): {
  body: string;
  headers: { [index: string]: any };
} {
  const [formBoundary] = createNewGuid();
  const formParams: string[] = [];
  formParams.push(`--${formBoundary}`);
  if (headers !== undefined) {
    Object.entries(headers).forEach(([key, value]) => {
      if (value !== undefined) {
        formParams.push(`${key}: ${value}`);
      }
    });
  }
  formParams.push(`X-HTTP-Method-Override: GET`);
  formParams.push(`\r\n--${formBoundary}--`);
  const postBody = formParams.join("\r\n");
  const header: { [index: string]: any } = {
    "Content-Type": `multipart/form-data;boundary=${formBoundary}`,
  };
  return { body: postBody, headers: header };
}
